// hooks/useBuilding.ts
// Fetch building data from API

import { useState, useEffect, useCallback } from 'react';
import { getBuilding, type Building, type Unit, type BuildingImage, type Testimonial } from '../lib/api';

interface UseBuildingResult {
  building: Building | null;
  units: Unit[];
  images: BuildingImage[];
  testimonials: Testimonial[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useBuilding(slug: string): UseBuildingResult {
  const [building, setBuilding] = useState<Building | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [images, setImages] = useState<BuildingImage[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchBuilding = useCallback(async () => {
    if (!slug) return;

    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await getBuilding(slug);

    if (fetchError || !data) {
      setError(new Error(fetchError || 'Building not found'));
      setBuilding(null);
      setUnits([]);
      setImages([]);
      setTestimonials([]);
    } else {
      setBuilding(data.building);
      setUnits(data.units || []);
      setImages(data.images || []);
      setTestimonials(data.testimonials || []);
    }

    setLoading(false);
  }, [slug]);

  useEffect(() => {
    fetchBuilding();
  }, [fetchBuilding]);

  return { building, units, images, testimonials, loading, error, refetch: fetchBuilding };
}
